import { useMemo, useRef, useLayoutEffect } from 'react'
import type { VatMode } from '@crm/shared'
import { buildKpItems } from '../utils/buildKpItems'
import { kpVatView } from '../utils/kpVat'
import { KP_VAT_TOGGLE_ENABLED, KpVatModeToggle } from './KpVatModeToggle'
import type { Request, RequestItem } from '../../../shared/api/types'
import type { SelectedPrice } from '../hooks/useKpMode'

const MATRIX_LABELS: Record<string, string> = {
  stock: 'Склад',
  china: 'Китай',
  rf: 'РФ',
  manuf: 'Производители',
  other: 'Стоки',
}

interface Props {
  request: Request
  visibleItems: RequestItem[]
  selectedPrices: Map<string, SelectedPrice>
  kpMarkups: Record<string, number>
  setKpMarkups: React.Dispatch<React.SetStateAction<Record<string, number>>>
  positionOverrides: Record<string, number>
  notes: Record<string, string>
  setNotes: React.Dispatch<React.SetStateAction<Record<string, string>>>
  /** Режим НДС КП — тот же переключатель, что в панели режима КП. */
  vatMode: VatMode
  setVatMode: (mode: VatMode) => void
  onClose: () => void
  onDownload: () => void | Promise<void>
}

// Превью КП в модальном окне: финальные цены для заказчика (себестоимость
// × наценка) + bulk-инпуты наценок прямо в модалке для live-итерации, чтобы
// менеджер не скачивал XLSX по 5 раз ради подбора множителей.
//
// Источник правды по выбранным ценам / позиционным override-наценкам — стейт
// из useKpMode (живёт в RequestDetailPage). Изменения markups здесь сразу
// летят в setKpMarkups, и таблица под фоном модалки тоже подхватит новые
// цифры. Позиционные наценки тут НЕ редактируются — для этого есть отдельный
// блок в KpModePanel; иначе модалка превью разрослась бы.
export function KPPreviewModal({
  request,
  visibleItems,
  selectedPrices,
  kpMarkups,
  setKpMarkups,
  positionOverrides,
  notes,
  setNotes,
  vatMode,
  setVatMode,
  onClose,
  onDownload,
}: Props) {
  const kpItems = useMemo(
    () =>
      buildKpItems({
        items: request.items ?? [],
        selectedPrices,
        kpMarkups,
        positionOverrides,
        vatMode,
      }),
    [request.items, selectedPrices, kpMarkups, positionOverrides, vatMode],
  )

  // Цены, суммы и итоги к показу — те же, что попадут в файл КП (kpVatView).
  const view = useMemo(() => kpVatView(kpItems, vatMode), [kpItems, vatMode])
  // Сумма с НДС — для средней наценки (себестоимость и наценка считаются от цены с НДС).
  const totalSum = useMemo(
    () => kpItems.reduce((sum, it) => sum + it.priceWithMarkup * it.quantity, 0),
    [kpItems],
  )

  // Общая себестоимость и средневзвешенная наценка (сумма / себестоимость).
  // kpItems пересобираются при каждом изменении наценок в инпутах выше,
  // поэтому средняя наценка пересчитывается на лету вместе с итогом.
  const totalCost = useMemo(
    () => kpItems.reduce((sum, it) => sum + it.cost * it.quantity, 0),
    [kpItems],
  )
  const avgMarkup = totalCost > 0 ? totalSum / totalCost : null

  const itemsCount = visibleItems.length
  const fmt = (n: number) =>
    new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Предпросмотр КП</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {request.client?.name ?? 'Клиент не указан'}
              {request.requestNumber ? ` · Заявка №${request.requestNumber}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            title="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-xs font-medium text-gray-600">
              Наценки (можно менять — цены ниже пересчитываются сразу)
            </div>
            {KP_VAT_TOGGLE_ENABLED && <KpVatModeToggle value={vatMode} onChange={setVatMode} />}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {(
              [
                ['stock', 'Склад'],
                ['other', 'Стоки'],
                ['rf', 'РФ'],
                ['china', 'Китай'],
                ['manuf', 'Производители'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex flex-col">
                <span className="text-xs text-gray-500 mb-0.5">{label}</span>
                <input
                  type="number"
                  step="0.01"
                  value={kpMarkups[key]}
                  onChange={(e) =>
                    setKpMarkups((prev) => ({
                      ...prev,
                      [key]: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </label>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {kpItems.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-12">
              Не выбрано ни одной позиции. Закройте превью и выберите цены в таблице или нажмите «По
              лучшим ценам» / «Выбрать все позиции».
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="border-b border-gray-200">
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 w-12">
                    №
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Арт.</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Бренд</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 w-20">
                    Кол-во
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-32">
                    Матрица
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 w-32">
                    Цена за ед. {view.priceNote}, ₽
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 w-36">
                    Сумма {view.priceNote}, ₽
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-64">
                    Примечание
                  </th>
                </tr>
              </thead>
              <tbody>
                {kpItems.map((it, idx) => {
                  const { price, sum } = view.lines[idx]!
                  const zero = it.priceWithMarkup === 0
                  return (
                    <tr
                      key={it.itemId}
                      className={`border-b border-gray-100 ${zero ? 'bg-amber-50/40' : ''}`}
                    >
                      <td className="px-3 py-1.5 text-center text-xs text-gray-500">{idx + 1}</td>
                      <td className="px-3 py-1.5 font-mono text-xs text-gray-800">
                        {it.partNumber}
                      </td>
                      <td className="px-3 py-1.5 text-gray-700">{it.brand ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700">
                        {it.quantity} {it.unit ?? 'pcs'}
                      </td>
                      <td className="px-3 py-1.5 text-gray-600">
                        {MATRIX_LABELS[it.matrix] ?? it.matrix}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right font-mono ${zero ? 'text-amber-600' : 'text-gray-900'}`}
                        title={zero ? 'Позиция без цены — попадёт в КП с 0' : undefined}
                      >
                        {fmt(price)}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right font-mono font-medium ${zero ? 'text-amber-600' : 'text-gray-900'}`}
                      >
                        {fmt(sum)}
                      </td>
                      <td className="px-3 py-1.5 align-top">
                        <NoteInput
                          value={notes[it.itemId] ?? it.note ?? ''}
                          onChange={(v) => setNotes((prev) => ({ ...prev, [it.itemId]: v }))}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot className="bg-gray-50 sticky bottom-0">
                {view.rows.map((row, i) => (
                  <tr key={row.label} className={i === 0 ? 'border-t-2 border-gray-300' : ''}>
                    <td colSpan={6} className="px-3 py-1.5 text-right">
                      {i === 0 && (
                        <>
                          <span
                            className="text-gray-400"
                            title="Общая себестоимость выбранных позиций"
                          >
                            Себестоимость: <span className="font-mono">{fmt(totalCost)} ₽</span>
                          </span>
                          <span
                            className="ml-4 text-gray-500"
                            title="Средняя наценка по КП (сумма с НДС / себестоимость), пересчитывается при изменении наценок"
                          >
                            Ср. наценка:{' '}
                            <span className="font-mono">
                              {avgMarkup !== null ? `×${avgMarkup.toFixed(2)}` : '—'}
                            </span>
                          </span>
                        </>
                      )}
                      <span
                        className={`ml-4 ${row.main ? 'font-medium text-gray-700' : 'text-gray-500'}`}
                      >
                        {row.label}
                      </span>
                    </td>
                    <td
                      className={`px-3 py-1.5 text-right font-mono ${row.main ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
                    >
                      {fmt(row.value)} ₽
                    </td>
                    <td className="px-3 py-1.5" />
                  </tr>
                ))}
              </tfoot>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-200">
          <span className="text-xs text-gray-500">
            Выбрано позиций: {kpItems.length} из {itemsCount}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Закрыть
            </button>
            <button
              onClick={() => void onDownload()}
              disabled={kpItems.length === 0}
              className="px-4 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              Скачать КП
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Поле примечания с авто-высотой: textarea растёт под введённый текст, чтобы
// длинное примечание было видно целиком (а не скроллилось в одну строку).
function NoteInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Примечание…"
      className="w-full resize-none overflow-hidden rounded border border-gray-300 px-2 py-1 text-sm leading-snug focus:outline-none focus:ring-1 focus:ring-brand-500"
    />
  )
}
